import React, { useState, useEffect, useMemo, useRef } from "react";
import styled from "styled-components";
import { Slider, InputNumber, Select, Progress, Card, Row, Col, Alert, Divider, Badge, Tooltip, Button } from "antd";
import { InfoCircleOutlined, ClockCircleOutlined, ReloadOutlined, WarningOutlined } from "@ant-design/icons";
import { formatCurrency } from "../../../helpers/retirementGoalCalculations";
import { rmdDistributionPeriods } from "../../../helpers/retirementGoalCalculations";
import { Line } from "react-chartjs-2";

// Reusing some logic from EducationProjection headers
import futurProjectionImg from "../../../assets/images/up_down.png";
import currencyImg from "../../../assets/images/currency3.png";
import breakdownImg from "../../../assets/images/performance.png";

const { Option } = Select;

const Title = styled.h3`
  margin-bottom: 0.5em !important;
  color: rgba(0, 0, 0, 0.85);
  font-weight: 600;
  font-size: ${props => props.level === 2 ? '30px' : props.level === 3 ? '24px' : '20px'};
`;

const Text = styled.span`
  color: ${props => props.type === 'secondary' ? 'rgba(0, 0, 0, 0.45)' : 'inherit'};
  font-weight: ${props => props.strong ? '600' : 'normal'};
`;

const Paragraph = styled.p`
  margin-bottom: 1em;
  color: ${props => props.type === 'secondary' ? 'rgba(0, 0, 0, 0.45)' : 'inherit'};
`;

const FlexSpace = styled.div`
  display: flex;
  flex-direction: ${props => props.direction === 'vertical' ? 'column' : 'row'};
  gap: ${props => props.size === 'middle' ? '16px' : '8px'};
  width: ${props => props.style && props.style.width ? props.style.width : 'auto'};
`;

const fadeInUp = `
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ModernContainer = styled.div`
  ${fadeInUp}
  padding: 0;
  border-radius: 16px;
  width: 100%;
  font-family: 'Poppins', sans-serif;
  background: #fff;
  overflow: hidden;
  animation: fadeInUp 0.6s ease-out;

  /* Target only InputNumber with the .Input class to fix height issues */
  .ant-input-number.Input {
    height: 32px !important;
  }
  .ant-input-number.Input .ant-input-number-input {
    height: 30px !important;
  }
`;

const ModernHeader = styled.div`
  background: white;
  padding: 2rem;
  border-bottom: 2px solid #f0f2f5;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  gap: 20px;
`;

const HeaderIcon = styled.div`
  width: 70px;
  height: 70px;
  background: rgba(42, 61, 163, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #2a3da3;
  font-size: 32px;
`;

const SectionTitle = styled(Divider)`
  margin: 40px 0 !important;
  .ant-divider-inner-text {
    font-size: 18px !important;
    font-weight: 600 !important;
    color: #495057 !important;
  }
`;

const SummaryCardRow = styled.div`
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  padding: 0 2rem;
  margin-bottom: 32px;
`;

const SummaryItem = styled.div`
  flex: 1;
  min-width: 280px;
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 24px;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-radius: 16px;
  transition: all 0.3s ease;
  border: 1px solid #f0f2f5;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0,0,0,0.02);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: ${props => props.color || "#2a3da3"};
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0,0,0,0.08);
  }
`;

const ItemDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

const ItemLabel = styled(Text)`
  font-size: 13px;
  color: #6c757d;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ItemValue = styled(Title)`
  margin: 0 !important;
  font-weight: 800 !important;
  font-size: 32px !important;
  color: ${props => props.color || "#2a3da3"} !important;
  
  &.rolling {
    transition: all 0.2s ease-in-out;
  }
`;

const StyledCard = styled(Card)`
  margin: 0 2rem 2rem 2rem;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.05);
  border: 1px solid #f0f2f5;
  overflow: hidden;

  .ant-card-head {
    background: #2a3da3;
    padding: 16px 24px;
    border: none;
  }
  .ant-card-head-title {
    color: white;
    font-weight: 700;
  }
`;

const LeverRow = styled(Row)`
  background: #fafafa;
  padding: 1.5rem;
  border-radius: 12px;
  margin-bottom: 1rem !important;
  align-items: center;
  border: 1px solid #f0f2f5;
  transition: all 0.3s ease;
  &:hover {
    background: #fff;
    border-color: #2a3da3;
    box-shadow: 0 4px 12px rgba(42, 61, 163, 0.08);
  }
`;

const ImpactBadge = styled(Badge)`
  .ant-badge-count {
    background: #e6f7ff;
    color: #1890ff;
    font-weight: 700;
    border: 1px solid #91d5ff;
  }
`;

const RevertIcon = styled(ReloadOutlined)`
  color: #adb5bd;
  cursor: pointer;
  margin-left: 8px;
  transition: all 0.2s;
  &:hover {
    color: #2a3da3;
    transform: rotate(-30deg);
  }
`;

const WarningText = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  color: #b45309;
  font-size: 13px;
  margin-bottom: 8px;
  background: #fffbeb;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid #fef3c7;
`;

const RetirementStrategyResults = ({
    payload,
    setPayload,
    taxInfo,
    taxInfoAllData,
    rateOfReturnData,
    assetsAssign = []
}) => {
    // Current Values (Defaults)
    const defaultRetireAge = parseFloat(payload["Desired Retirement Age"]) || 0;
    const defaultAnnualBudget = parseFloat(payload["Net Annual Post-Retirement Budget"]) || 0;
    const defaultMonthlySavings = parseFloat(payload["Your Commitment"]) || 0;

    // Solver Inputs
    const [retireAge, setRetireAge] = useState(defaultRetireAge);
    const [annualBudget, setAnnualBudget] = useState(defaultAnnualBudget);
    const [monthlySavings, setMonthlySavings] = useState(defaultMonthlySavings);
    const [targetAccount, setTargetAccount] = useState(payload["Target Account"] || "");

    // Calculation Results
    const [depletionAge, setDepletionAge] = useState(null);
    const [projectedIncome, setProjectedIncome] = useState(0);
    const [coveragePercent, setCoveragePercent] = useState(0);
    const [impactMap, setImpactMap] = useState({ age: 0, budget: 0, savings: 0 });
    const [chartData, setChartData] = useState(null);
    const [warnings, setWarnings] = useState([]);

    // Ref for rolling number animation
    const [displayDepletionAge, setDisplayDepletionAge] = useState(null);
    const rollingRef = useRef(null);

    const lifeExpectancy = parseFloat(payload["Client Life Expectancy"]) || 0;
    const currentAge = parseFloat(payload["Current Age"]) || 0;
    const totalIncome = parseFloat(payload["Your Annual Income"]) || 0;

    // Numerical Animation for Hero Metric
    useEffect(() => {
        if (depletionAge === null) return;

        if (displayDepletionAge === null) {
            setDisplayDepletionAge(depletionAge);
            return;
        }

        if (rollingRef.current) clearInterval(rollingRef.current);

        const start = displayDepletionAge;
        const end = depletionAge;
        const duration = 400;
        const steps = 20;
        const stepTime = duration / steps;
        let currentStep = 0;

        rollingRef.current = setInterval(() => {
            currentStep++;
            const progress = currentStep / steps;
            const value = Math.round(start + (end - start) * progress);
            setDisplayDepletionAge(value);

            if (currentStep >= steps) {
                setDisplayDepletionAge(end);
                clearInterval(rollingRef.current);
            }
        }, stepTime);

        return () => clearInterval(rollingRef.current);
    }, [depletionAge]);

    // Document 3: Logical Flow of Operations (Full Year-over-Year Loop)
    const runFullProjection = (params) => {
        const { rAge, budget, savings } = params;

        // Initialization
        const startYear = new Date().getFullYear();
        const inflationInput = payload["Inflation rate for this goal"];
        const matchedInflation = taxInfo && taxInfo.find((item) => item.name === inflationInput);
        const inflationRate = matchedInflation ? (parseFloat(matchedInflation.value) || 0) / 100 : 0.03;

        const returnRate = (payload["Assumed Annual Investment Return (%)"]) || (rateOfReturnData && rateOfReturnData.staticPercentage);
        const growthRate = returnRate !== undefined ? Number(returnRate) / 100 : 0.07;

        // Assets
        let portfolioBalance = assetsAssign
            .filter(a => a.listType === "liquid")
            .reduce((sum, a) => sum + (parseFloat(a.valueOfAssetGoalRate) || 0), 0);

        const illiquidAssets = assetsAssign.filter(a => a.listType === "illiquid");

        // Income Sources
        const ssUser = parseFloat(payload["Your Estimated Social Security"]) || 0;
        const ssSpouse = parseFloat(payload["Social Security - Spouse"]) || 0;
        const pension = parseFloat(payload["Pension"]) || 0;
        const rental = parseFloat(payload["Net Rental Income"]) || 0;

        let additionalIncomes = [];
        if (Array.isArray(payload["Annual Amount"])) {
            additionalIncomes = payload["Annual Amount"];
        } else if (payload["Annual Amount"]) {
            additionalIncomes = [payload["Annual Amount"]];
        }
        const sumAdditionalIncome = additionalIncomes.reduce((s, v) => s + (parseFloat(v) || 0), 0);

        // Result Containers
        let fundsDepletedAge = 110;
        const trajectory = [];
        let reachedZero = false;

        // Start Calculation Loop
        for (let age = currentAge; age <= 110; age++) {
            const yearPassed = age - currentAge;
            const currentYear = startYear + yearPassed;
            const status = age < rAge ? "Working" : "Retired";

            // Step 2: Apply Inflation to Expenses
            // Note: Expenses only occur in retirement in this model
            const yearlyExpense = status === "Retired" ? budget * Math.pow(1 + inflationRate, yearPassed) : 0;

            // Step 3: Liquidity Event (Illiquid Asset Sale)
            illiquidAssets.forEach(asset => {
                if (parseInt(asset.saleYear) === currentYear && asset.Strategy === "Sell to Fund") {
                    portfolioBalance += parseFloat(asset.netLumpSum) || 0;
                }
            });

            // Step 4: Process Inflows
            let totalFixedIncome = 0;
            if (status === "Working") {
                portfolioBalance += (savings * 12); // New commitment from Form 5
            } else {
                // Fixed Income in Retirement
                totalFixedIncome = (ssUser + ssSpouse + pension + rental + sumAdditionalIncome) * Math.pow(1 + inflationRate, yearPassed);
            }

            // Step 5: Process Outflows (The Gap)
            if (status === "Retired") {
                const gap = yearlyExpense - totalFixedIncome;
                portfolioBalance -= gap; // Subtract deficit or add surplus
            }

            // Step 6: Apply Investment Growth
            // Do not apply growth if already hit zero
            if (portfolioBalance > 0) {
                const returns = portfolioBalance * growthRate;
                portfolioBalance += returns;
            }

            // Track Trajectory
            trajectory.push({ age, balance: Math.max(0, portfolioBalance) });

            // Step 7: Solvency Check
            if (portfolioBalance <= 0 && !reachedZero) {
                fundsDepletedAge = age;
                reachedZero = true;
                // We keep looping to finish the chart data, but record the age
            }
        }

        return {
            depletionAge: reachedZero ? fundsDepletedAge : 100, // "100+" display logic
            trajectory,
            firstYearFixedIncome: (ssUser + ssSpouse + pension + rental + sumAdditionalIncome) * Math.pow(1 + inflationRate, rAge - currentAge)
        };
    };

    // Document 2: Guardrails & Warnings Logic
    const validatePlan = (params) => {
        const { rAge, budget, savings } = params;
        const activeWarnings = [];

        // 1. Contribution Limits (~23.5k for 401k)
        if (savings * 12 > 23500 && targetAccount.includes("401k")) {
            activeWarnings.push({
                category: "Contribution Limits",
                message: "Exceeds Limit: The max annual contribution for this account type is approx. $23,500. Please lower the amount or choose a taxable account."
            });
        }

        // 2. Income Reality
        const currentExpenses = parseFloat(payload["Current Monthly Expenses"]) || 0;
        if ((savings + currentExpenses) * 12 > totalIncome) {
            activeWarnings.push({
                category: "Income Reality",
                message: "Impossible Plan: You are attempting to spend/save more money than you earn. Please check your Income or Budget."
            });
        }

        // 3. RMD Conflict
        if (rAge > 73) {
            activeWarnings.push({
                category: "RMD Conflict",
                message: "Note: Required Minimum Distributions (RMDs) begin at age 73. You will be forced to take taxable withdrawals even if you are still working."
            });
        }

        // 4. Return Assumptions
        const returnRate = parseFloat(payload["Assumed Annual Investment Return (%)"]);
        if (returnRate > 12 || returnRate < -2) {
            activeWarnings.push({
                category: "Return Assumptions",
                message: "Unusual Return Rate: Historic market averages are typically 5-10%. Ensure this value is intentional."
            });
        }

        // 5. Logic Conflict
        if (rAge < currentAge) {
            activeWarnings.push({
                category: "Logic Conflict",
                message: "Goal Date must be in the future (Retirement age must be greater than current age)."
            });
        }

        setWarnings(activeWarnings);
    };

    useEffect(() => {
        const results = runFullProjection({ rAge: retireAge, budget: annualBudget, savings: monthlySavings });
        setDepletionAge(results.depletionAge);
        setProjectedIncome(results.firstYearFixedIncome);
        setCoveragePercent(Math.min(100, Math.round((results.firstYearFixedIncome / (annualBudget || 1)) * 100)));

        // Impact Map Calculations
        const impactAge = runFullProjection({ rAge: retireAge + 1, budget: annualBudget, savings: monthlySavings }).depletionAge - results.depletionAge;
        const impactBudget = runFullProjection({ rAge: retireAge, budget: annualBudget - 2400, savings: monthlySavings }).depletionAge - results.depletionAge;
        const impactSavings = runFullProjection({ rAge: retireAge, budget: annualBudget, savings: monthlySavings + 500 }).depletionAge - results.depletionAge;

        setImpactMap({ age: impactAge, budget: impactBudget, savings: impactSavings });

        // Update Chart
        setChartData({
            labels: results.trajectory.map(t => `Age ${t.age}`),
            datasets: [
                {
                    label: "Portfolio Balance",
                    data: results.trajectory.map(t => t.balance),
                    borderColor: "#2a3da3",
                    backgroundColor: "rgba(42, 61, 163, 0.1)",
                    fill: true,
                    tension: 0.4,
                    pointRadius: 0
                }
            ]
        });

        validatePlan({ rAge: retireAge, budget: annualBudget, savings: monthlySavings });

    }, [retireAge, annualBudget, monthlySavings]);

    // ===================================================================
    // REMOVED: Automatic Payload Update (Test Case 4 Fix)
    // ===================================================================
    // This useEffect was automatically updating the global payload, which
    // violated the requirement that Form 5 should be a "hypothetical" scenario.
    // Changes should only be saved when the user explicitly clicks "Save Changes".
    //
    // useEffect(() => {
    //   setPayload(prev => ({
    //     ...prev,
    //     "Desired Retirement Age": retireAge,
    //     "Net Annual Post-Retirement Budget": annualBudget,
    //     "Your Commitment": monthlySavings,
    //     "Target Account": targetAccount,
    //     "Funds Last Until Age": depletionAge
    //   }));
    // }, [retireAge, annualBudget, monthlySavings, targetAccount, depletionAge, setPayload]);

    // Manual save function (to be called by "Save Changes" button)
    const handleSaveScenario = () => {
        // Calculate the additional monthly savings (difference from original)
        const additionalMonthlySavings = Math.max(0, monthlySavings - defaultMonthlySavings);

        // Calculate monthly gap (shortfall between budget and projected income)
        const monthlyBudget = annualBudget / 12;
        const monthlyIncome = projectedIncome / 12;
        const monthlyGap = Math.max(0, monthlyBudget - monthlyIncome);
        const remainingGap = Math.max(0, monthlyGap - monthlySavings);

        // Update all Form 5 (ADD SAVINGS) attributes
        const updatedPayload = {
            ...payload,
            // Core scenario values (what user adjusted)
            "Desired Retirement Age": retireAge,
            "Net Annual Post-Retirement Budget": annualBudget,
            "Your Commitment": monthlySavings,
            "Target Account": targetAccount,

            // Calculated results (from Form 5 solver)
            "Funds Last Until Age": depletionAge,
            "Current Monthly Gap": monthlyGap,
            "Suggested Savings": monthlySavings, // The solver's suggested amount
            "Remaining Monthly Gap": remainingGap,
        };

        setPayload(updatedPayload);

        console.log("[Form 5] Scenario saved to global payload:", {
            retireAge,
            annualBudget,
            monthlySavings,
            targetAccount,
            depletionAge,
            monthlyGap,
            remainingGap,
            additionalSavings: additionalMonthlySavings
        });

        // Show success message to user
        alert(`✅ Strategy Saved Successfully!\n\n` +
            `Retirement Age: ${retireAge}\n` +
            `Monthly Budget: $${monthlyBudget.toFixed(2)}\n` +
            `Monthly Savings: $${monthlySavings.toFixed(2)}\n` +
            `Funds Last Until: Age ${depletionAge >= 100 ? '100+' : depletionAge}\n\n` +
            `Your plan has been updated with these changes.`);
    };

    const liquidAccounts = assetsAssign.filter(a => a.listType === "liquid");
    const verdictColor = depletionAge >= lifeExpectancy ? "#51cf66" : (depletionAge > 80 ? "#ffa94d" : "#ff6b6b");

    return (
        <ModernContainer>
            <ModernHeader>
                <HeaderIcon>
                    <ClockCircleOutlined />
                </HeaderIcon>
                <div>
                    <Title level={3} style={{ margin: 0 }}>Retirement Results & Strategy Optimization</Title>
                    <Paragraph style={{ margin: 0, color: "#6c757d" }}>
                        Interactive solver to maximize your retirement solvency and close the income gap.
                    </Paragraph>
                </div>
            </ModernHeader>

            <SummaryCardRow>
                <SummaryItem color={verdictColor}>
                    <div style={{ width: 64, height: 64, flexShrink: 0 }}>
                        <img width={64} height={64} src={futurProjectionImg} alt="Depletion" />
                    </div>
                    <ItemDetails>
                        <ItemLabel>Projected Depletion Age</ItemLabel>
                        <ItemValue level={3} color={verdictColor} className="rolling">
                            Age {displayDepletionAge === 100 ? "100+" : displayDepletionAge}
                        </ItemValue>
                        <Badge
                            status={depletionAge >= lifeExpectancy ? "success" : "error"}
                            text={depletionAge >= lifeExpectancy ? "Goal Met!" : `${lifeExpectancy - depletionAge}yr Shortfall`}
                        />
                    </ItemDetails>
                </SummaryItem>

                <SummaryItem color="#108ee9">
                    <div style={{ width: 64, height: 64, flexShrink: 0 }}>
                        <img width={64} height={64} src={breakdownImg} alt="Coverage" />
                    </div>
                    <ItemDetails>
                        <ItemLabel>Income Coverage</ItemLabel>
                        <ItemValue level={3} color="#108ee9">{coveragePercent}%</ItemValue>
                        <Badge count="Reliability" style={{ backgroundColor: '#108ee9' }} />
                    </ItemDetails>
                </SummaryItem>

                <SummaryItem color="#51cf66">
                    <div style={{ width: 64, height: 64, flexShrink: 0 }}>
                        <img width={64} height={64} src={currencyImg} alt="Income" />
                    </div>
                    <ItemDetails>
                        <ItemLabel>Monthly Income</ItemLabel>
                        <ItemValue level={3} color="#51cf66">{formatCurrency(projectedIncome / 12)}</ItemValue>
                        <Text type="secondary" style={{ fontSize: 11 }}>Target: {formatCurrency(annualBudget / 12)}</Text>
                    </ItemDetails>
                </SummaryItem>
            </SummaryCardRow>

            <SectionTitle orientation="left">Visual Analytics</SectionTitle>

            <StyledCard title="Wealth Path (Portfolio Balance over Time)">
                <div style={{ height: 300 }}>
                    {chartData && (
                        <Line
                            data={chartData}
                            options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                scales: {
                                    yAxes: [{
                                        ticks: {
                                            beginAtZero: true,
                                            callback: (value) => '$' + (value / 1000000).toFixed(1) + 'M'
                                        }
                                    }]
                                },
                                legend: {
                                    display: false
                                }
                            }}
                        />
                    )}
                </div>
                <div style={{ marginTop: 16 }}>
                    <Progress
                        percent={coveragePercent}
                        status={depletionAge >= lifeExpectancy ? "success" : "active"}
                        strokeColor={{ '0%': '#108ee9', '100%': '#52c41a' }}
                        strokeWidth={12}
                    />
                    <div style={{ textAlign: "center", marginTop: 8 }}>
                        <Text strong type="secondary">Income Reliability Gap</Text>
                    </div>
                    <Row gutter={32} style={{ marginTop: 24, textAlign: 'center' }}>
                        <Col span={8}>
                            <Text type="secondary" style={{ fontSize: '13px', display: 'block', marginBottom: '4px' }}>Target Monthly Income</Text>
                            <Title level={4} style={{ margin: 0 }}>{formatCurrency(annualBudget / 12)}</Title>
                        </Col>
                        <Col span={8}>
                            <Text type="secondary" style={{ fontSize: '13px', display: 'block', marginBottom: '4px' }}>Projected Base Income</Text>
                            <Title level={4} style={{ margin: 0 }}>{formatCurrency(projectedIncome / 12)}</Title>
                        </Col>
                        <Col span={8}>
                            <Text type="secondary" style={{ fontSize: '13px', display: 'block', marginBottom: '4px' }}>Monthly Shortfall</Text>
                            <Title level={4} style={{ margin: 0, color: "#ff6b6b" }}>{formatCurrency(Math.max(0, (annualBudget - projectedIncome) / 12))}</Title>
                        </Col>
                    </Row>
                </div>
            </StyledCard>

            <SectionTitle orientation="left">The Strategy Solver</SectionTitle>

            <StyledCard title="Interactive Levers (Instant Impact)">
                <Paragraph type="secondary" style={{ marginBottom: 24 }}>
                    Adjust the levers below to see real-time updates to your retirement solvency age.
                </Paragraph>

                {warnings.length > 0 && (
                    <div style={{ marginBottom: 20 }}>
                        {warnings.map((w, idx) => (
                            <WarningText key={idx}>
                                <WarningOutlined />
                                <span>{w.message}</span>
                            </WarningText>
                        ))}
                    </div>
                )}

                <FlexSpace direction="vertical" style={{ width: '100%' }} size="middle">
                    <LeverRow gutter={24}>
                        <Col span={5}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <Text strong style={{ fontSize: 16 }}>Retire Later</Text>
                                <Tooltip title="Revert to original plan">
                                    <RevertIcon onClick={() => setRetireAge(defaultRetireAge)} />
                                </Tooltip>
                            </div>
                            <Text type="secondary">Plan: Age {defaultRetireAge}</Text>
                        </Col>
                        <Col span={10}>
                            <Slider
                                min={Math.max(0, currentAge)}
                                max={85}
                                value={retireAge}
                                onChange={setRetireAge}
                                trackStyle={{ backgroundColor: "#2a3da3" }}
                                handleStyle={{ borderColor: "#2a3da3", backgroundColor: "#fff" }}
                            />
                        </Col>
                        <Col span={5}>
                            <InputNumber
                                className="Input"
                                min={Math.max(0, currentAge)}
                                max={85}
                                value={retireAge}
                                onChange={setRetireAge}
                                style={{ width: '100%' }}
                                formatter={v => `Age ${v}`}
                                parser={v => v.replace(/Age\s?|[^\d.]/g, '')}
                            />
                        </Col>
                        <Col span={4}>
                            <ImpactBadge count={`${impactMap.age >= 0 ? '+' : ''}${(impactMap.age).toFixed(1)}yrs Impact`} />
                        </Col>
                    </LeverRow>

                    <LeverRow gutter={24}>
                        <Col span={5}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <Text strong style={{ fontSize: 16 }}>Spend Less</Text>
                                <Tooltip title="Revert to original plan">
                                    <RevertIcon onClick={() => setAnnualBudget(defaultAnnualBudget)} />
                                </Tooltip>
                            </div>
                            <Text type="secondary">{formatCurrency(defaultAnnualBudget / 12)} / mo</Text>
                        </Col>
                        <Col span={10}>
                            <Slider
                                min={0}
                                max={Math.max(50000, defaultAnnualBudget / 12 + 10000)}
                                step={100}
                                value={Math.round(annualBudget / 12)}
                                onChange={v => setAnnualBudget(v * 12)}
                                trackStyle={{ backgroundColor: "#2a3da3" }}
                                handleStyle={{ borderColor: "#2a3da3", backgroundColor: "#fff" }}
                            />
                        </Col>
                        <Col span={5}>
                            <InputNumber
                                className="Input"
                                min={0}
                                max={50000}
                                value={Math.round(annualBudget / 12)}
                                onChange={v => setAnnualBudget(v * 12)}
                                style={{ width: '100%' }}
                                formatter={v => `$ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                parser={v => v.replace(/\$\s?|(\,*)/g, '')}
                            />
                        </Col>
                        <Col span={4}>
                            <ImpactBadge count={`${impactMap.budget >= 0 ? '+' : ''}${(impactMap.budget).toFixed(1)}yrs Impact`} />
                        </Col>
                    </LeverRow>

                    <LeverRow gutter={24}>
                        <Col span={5}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <Text strong style={{ fontSize: 16 }}>Save More</Text>
                                <Tooltip title="Revert to original plan">
                                    <RevertIcon onClick={() => setMonthlySavings(defaultMonthlySavings)} />
                                </Tooltip>
                            </div>
                            <Text type="secondary">{formatCurrency(defaultMonthlySavings)} / mo</Text>
                        </Col>
                        <Col span={10}>
                            <Slider
                                min={0}
                                max={Math.max(30000, defaultMonthlySavings + 5000)}
                                step={50}
                                value={monthlySavings}
                                onChange={setMonthlySavings}
                                trackStyle={{ backgroundColor: "#2a3da3" }}
                                handleStyle={{ borderColor: "#2a3da3", backgroundColor: "#fff" }}
                            />
                        </Col>
                        <Col span={5}>
                            <InputNumber
                                className="Input"
                                min={0}
                                max={30000}
                                value={monthlySavings}
                                onChange={setMonthlySavings}
                                style={{ width: '100%' }}
                                formatter={v => `$ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                parser={v => v.replace(/\$\s?|(\,*)/g, '')}
                            />
                        </Col>
                        <Col span={4}>
                            <ImpactBadge count={`${impactMap.savings >= 0 ? '+' : ''}${(impactMap.savings).toFixed(1)}yrs Impact`} />
                        </Col>
                    </LeverRow>
                </FlexSpace>

                <Alert
                    message={
                        <span style={{ fontWeight: 600 }}>
                            Strategy Outcome: With these adjustments, your assets last until Age {depletionAge === 100 ? "100+" : depletionAge}.
                            {depletionAge >= lifeExpectancy ? " (Target Met! ✅)" : " (Working towards gap closure)"}
                        </span>
                    }
                    type={depletionAge >= lifeExpectancy ? "success" : "info"}
                    showIcon
                    style={{ marginTop: 24, borderRadius: 12 }}
                />
            </StyledCard>

            <SectionTitle orientation="left">Implementation Plan</SectionTitle>

            <StyledCard title="Action Commitment">
                <Row gutter={48} align="middle">
                    <Col span={12}>
                        <Text type="secondary" style={{ textTransform: 'uppercase', fontSize: 12, fontWeight: 700 }}>Additional Monthly Savings Required</Text>
                        <Title level={2} style={{ margin: "8px 0 0 0", color: "#2a3da3" }}>
                            {formatCurrency(Math.max(0, monthlySavings - defaultMonthlySavings))}
                        </Title>
                        <Paragraph style={{ marginTop: 8, fontSize: 12, italic: true }}>
                            * This commitment will generate an action item on your dashboard.
                        </Paragraph>
                    </Col>
                    <Col span={12}>
                        <Text strong>Destination Account</Text>
                        <Select
                            style={{ width: '100%', marginTop: 8 }}
                            placeholder="Select Account"
                            value={targetAccount}
                            onChange={setTargetAccount}
                            size="large"
                        >
                            {liquidAccounts.map(acc => (
                                <Option key={acc.id} value={acc.assetName}>{acc.assetName}</Option>
                            ))}
                            <Option value="New Vanguard 401k (Tax-Deferred)">New Vanguard 401k (Tax-Deferred)</Option>
                            <Option value="New Roth IRA (Tax-Free)">New Roth IRA (Tax-Free)</Option>
                        </Select>
                        {targetAccount && (
                            <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center', color: '#1890ff' }}>
                                <InfoCircleOutlined />
                                <Text type="info" style={{ fontSize: 12 }}>
                                    {targetAccount.includes("Deferred") ? "Reduces your current taxable income." : "Allows for tax-free growth and withdrawals."}
                                </Text>
                            </div>
                        )}
                    </Col>
                </Row>
            </StyledCard>

            {/* Save Changes Button (Test Case 4 Fix) */}
            <div style={{ padding: "0 2rem 2rem 2rem", display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '20px' }}>
                <Text type="secondary" italic>
                    * Projection assumes a {parseFloat(payload["Assumed Annual Investment Return (%)"]) || (rateOfReturnData && rateOfReturnData.staticPercentage) || 7}% rate of return.
                </Text>
                <Button
                    type="primary"
                    size="large"
                    onClick={handleSaveScenario}
                    style={{
                        background: '#2a3da3',
                        borderColor: '#2a3da3',
                        borderRadius: 8,
                        height: 48,
                        paddingInline: 40,
                        fontWeight: 600,
                        fontSize: 16,
                        boxShadow: '0 4px 12px rgba(42, 61, 163, 0.3)'
                    }}
                >
                    Save This Strategy to Plan
                </Button>
            </div>
        </ModernContainer>
    );
};

export default RetirementStrategyResults;

